import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Phone, Clock, Sparkles, ArrowRight } from 'lucide-react';
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}
interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ChatModal({
  isOpen,
  onClose
}: ChatModalProps) {
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    text: '¡Hola! 👋 Soy tu asesora virtual de NETCGSolutions. ¿En qué puedo ayudarte hoy?',
    sender: 'assistant',
    timestamp: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSend = () => {
    if (!inputValue.trim()) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    // Simulate assistant response
    setTimeout(() => {
      setIsTyping(false);
      const responses = ['Gracias por tu mensaje. Un asesor se pondrá en contacto contigo en menos de 24 horas. 📞', 'Entiendo tu consulta. ¿Podrías darme más detalles sobre tu situación? 🤔', 'Perfecto. Te recomiendo agendar una llamada para analizar tu caso en detalle. 📅', 'Excelente pregunta. Nuestro equipo especializado puede ayudarte con eso. ✨'];
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: randomResponse,
        sender: 'assistant',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    }, 1500);
  };
  const quickActions = [{
    label: '📋 Visados',
    action: 'Necesito información sobre visados',
    icon: '📋'
  }, {
    label: '🏠 Residencia',
    action: 'Quiero consultar sobre permisos de residencia',
    icon: '🏠'
  }, {
    label: '🏢 Empresas',
    action: 'Soy una empresa y necesito asesoría',
    icon: '🏢'
  }, {
    label: '💰 Presupuesto',
    action: 'Solicitar presupuesto',
    icon: '💰'
  }];
  return <AnimatePresence data-id="element-31">
      {isOpen && <>
          {/* Backdrop - Modern blur */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-md z-50" data-id="element-32" />

          {/* Modal - Modern glassmorphism */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 400
      }} className="fixed bottom-8 right-8 w-[420px] h-[650px] bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl z-50 flex flex-col overflow-hidden border border-white/20" style={{
        maxWidth: 'calc(100vw - 64px)',
        maxHeight: 'calc(100vh - 64px)'
      }} data-id="element-33">
            {/* Header - Modern gradient with glassmorphism */}
            <div className="relative bg-gradient-to-br from-navy via-steel to-indigo p-6 text-white overflow-hidden" data-id="element-34">
              {/* Animated background elements */}
              <div className="absolute inset-0 opacity-20" data-id="element-35">
                <motion.div className="absolute top-0 right-0 w-40 h-40 bg-primary rounded-full blur-3xl" animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3]
            }} transition={{
              duration: 4,
              repeat: Infinity
            }} data-id="element-36" />
                <motion.div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo rounded-full blur-3xl" animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.5, 0.3]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              delay: 1
            }} data-id="element-37" />
              </div>

              <div className="relative flex items-center justify-between" data-id="element-38">
                <div className="flex items-center gap-4" data-id="element-39">
                  <div className="relative" data-id="element-40">
                    <motion.div className="w-14 h-14 rounded-2xl overflow-hidden ring-2 ring-white/30 shadow-xl" whileHover={{
                  scale: 1.05
                }} data-id="element-41">
                      <img src="https://cdn.magicpatterns.com/uploads/wTkjRNYGvfvZ13Kbit9Piz/WhatsApp_Image_2026-01-04_at_19.53.47.jpg" alt="Asesor" className="w-full h-full object-cover" data-id="element-42" />
                    </motion.div>
                    <motion.span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-lg" animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} data-id="element-43" />
                  </div>
                  <div data-id="element-44">
                    <h3 className="font-bold text-lg flex items-center gap-2" data-id="element-45">
                      Asesor Virtual
                      <Sparkles className="w-4 h-4 text-yellow-300" data-id="element-46" />
                    </h3>
                    <p className="text-xs text-white/90 flex items-center gap-1.5 mt-0.5" data-id="element-47">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-48" />
                      Disponible ahora
                    </p>
                  </div>
                </div>
                <motion.button onClick={onClose} className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all" whileHover={{
              scale: 1.05,
              rotate: 90
            }} whileTap={{
              scale: 0.95
            }} data-id="element-49">
                  <X className="w-5 h-5" data-id="element-50" />
                </motion.button>
              </div>

              {/* Quick Info Pills */}
              <div className="mt-4 flex gap-2" data-id="element-51">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-xs font-medium" data-id="element-52">
                  <Phone className="w-3 h-3" data-id="element-53" />
                  <span data-id="element-54">+34 618 940 149</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-xs font-medium" data-id="element-55">
                  <Clock className="w-3 h-3" data-id="element-56" />
                  <span data-id="element-57">24-48h</span>
                </div>
              </div>
            </div>

            {/* Messages Area - Modern spacing */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-58">
              <AnimatePresence mode="popLayout" data-id="element-59">
                {messages.map((message, index) => <motion.div key={message.id} initial={{
              opacity: 0,
              y: 20,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30,
              delay: index * 0.05
            }} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-60">
                    <motion.div whileHover={{
                scale: 1.02
              }} className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${message.sender === 'user' ? 'bg-gradient-to-br from-primary to-orange-500 text-white' : 'bg-white text-gray-900 border border-gray-100'}`} data-id="element-61">
                      <p className="text-sm leading-relaxed" data-id="element-62">{message.text}</p>
                      <p className={`text-xs mt-1.5 ${message.sender === 'user' ? 'text-white/70' : 'text-gray-400'}`} data-id="element-63">
                        {message.timestamp.toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </p>
                    </motion.div>
                  </motion.div>)}
              </AnimatePresence>

              {/* Typing Indicator */}
              {isTyping && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex justify-start" data-id="element-64">
                  <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100" data-id="element-65">
                    <div className="flex gap-1" data-id="element-66">
                      {[0, 1, 2].map(i => <motion.div key={i} className="w-2 h-2 bg-gray-400 rounded-full" animate={{
                  y: [0, -8, 0]
                }} transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: i * 0.15
                }} data-id="element-67" />)}
                    </div>
                  </div>
                </motion.div>}

              <div ref={messagesEndRef} data-id="element-68" />
            </div>

            {/* Quick Actions - Modern chips */}
            {messages.length <= 2 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="px-6 py-4 bg-white/80 backdrop-blur-sm border-t border-gray-100" data-id="element-69">
                <p className="text-xs font-medium text-gray-500 mb-3" data-id="element-70">
                  Consultas frecuentes:
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-71">
                  {quickActions.map((action, index) => <motion.button key={action.label} onClick={() => {
              setInputValue(action.action);
              handleSend();
            }} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: index * 0.1
            }} whileHover={{
              scale: 1.05,
              y: -2
            }} whileTap={{
              scale: 0.95
            }} className="group px-4 py-2 text-xs font-medium bg-gradient-to-r from-gray-50 to-white hover:from-primary/5 hover:to-orange-500/5 text-gray-700 rounded-full transition-all border border-gray-200 hover:border-primary/30 shadow-sm hover:shadow-md" data-id="element-72">
                      <span className="flex items-center gap-1.5" data-id="element-73">
                        {action.label}
                        <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-74" />
                      </span>
                    </motion.button>)}
                </div>
              </motion.div>}

            {/* Input Area - Modern design */}
            <div className="p-6 bg-white border-t border-gray-100" data-id="element-75">
              <div className="flex gap-3" data-id="element-76">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend()} placeholder="Escribe tu mensaje..." className="flex-1 px-4 py-3.5 rounded-2xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-sm placeholder:text-gray-400" data-id="element-77" />
                <motion.button onClick={handleSend} disabled={!inputValue.trim()} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-orange-500 text-white flex items-center justify-center shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" data-id="element-78">
                  <Send className="w-5 h-5" data-id="element-79" />
                </motion.button>
              </div>
              <p className="text-xs text-gray-400 mt-3 text-center" data-id="element-80">
                ⚡ Respuesta típica en 24-48 horas
              </p>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}